import SelectorEngine from '../../../utils/dom/selector-engine';
import { Events } from '../../../utils/dom/html-events';
import { ClassNames } from '../../../utils/dom/css-classes';
import Request, { ApiMode } from '../../../utils/request';
import FormGenerator, { FormTypes } from '../../../components/FormGenerator';
import EventHandler from '../../../utils/dom/event-handler';
import Modal from '.../../../components/Modal';
import { startLoadingAnimation, stopLoadingAnimation } from '../../../utils/dom/loading';
import Manipulator from '../../../utils/dom/manipulator';

const SELECTOR_WRAPPER = '.p-coach-learning-journey';
const SELECTOR_MODAL = '.js-coach-learning-journey-modal';

const SELECTOR_SPLASH = '.p-coach-learning-journey-splash';
const SELECTOR_SPLASH_NEXT = '.p-coach-learning-journey-splash-next';

const SELECTOR_STEP = '.p-coach-learning-journey-step';
const SELECTOR_QUESTION = '.p-coach-learning-journey-questions';

const SELECTOR_SUCCESS = '.p-coach-learning-journey__success-page';
const CASE_ID = '.p-coach-learning-journey__caseId';
const SELECTOR_SUCCESS_BUTTON = '.p-coach-learning-journey__success-page-btn';

const SELECTOR_EXIT_BUTTON = '.js-exit-coach-learning-journey';
const SELECTOR_EXIT_CONFIRM_MODAL = '.js-exit-coach-learning-journey-confirm';
const SELECTOR_EXIT_CONFIRM_YES = '.js-exit-coach-learning-journey-confirm-yes';
const SELECTOR_EXIT_CONFIRM_NO = '.js-exit-coach-learning-journey-confirm-no';
const SELECTOR_ERRROR_CONTAINER = '.p-coach-learning-journey-error-container';

const IS_LOCAL = false;
const KEY_OBJECT = 'fieldName';
const VALUE_OBJECT = 'value';
const HIDDEN_CLASS = 'u-hidden';

class CoachesLearningJourney {
	constructor() {
		this._wrapper = SelectorEngine.findOne(SELECTOR_WRAPPER);
		if (!this._wrapper) {
			return;
		}
		this.init();
	}

	get stepsData() {
		return this._stepsData;
	}

	set stepsData(_stepsData) {
		this._stepsData = _stepsData;
		if (_stepsData?.length > 0) {
			this.initiateFormGenerator(_stepsData);
		}
	}

	init() {
		this.modalElement = SelectorEngine.findOne(SELECTOR_MODAL, this._wrapper);
		this.modal = new Modal(this.modalElement, { backdrop: 'static' });
		this.modal.show();

		this.splash = SelectorEngine.findOne(SELECTOR_SPLASH, this._wrapper);
		this.splashNext = SelectorEngine.findOne(SELECTOR_SPLASH_NEXT, this.splash);

		this.step = SelectorEngine.findOne(SELECTOR_STEP, this._wrapper);
		this.questions = SelectorEngine.findOne(SELECTOR_QUESTION, this.step);

		this.exitButton = SelectorEngine.findOne(SELECTOR_EXIT_BUTTON, this._wrapper);
		const exitConfirmModalHtml = SelectorEngine.findOne(SELECTOR_EXIT_CONFIRM_MODAL, this._wrapper);
		this.exitConfirmModal = new Modal(exitConfirmModalHtml, { backdrop: 'static' });

		this.exitConfirmYes = SelectorEngine.findOne(SELECTOR_EXIT_CONFIRM_YES, this._wrapper);
		this.exitConfirmNo = SelectorEngine.findOne(SELECTOR_EXIT_CONFIRM_NO, this._wrapper);

		this.coachLearningJourneySuccess = SelectorEngine.findOne(SELECTOR_SUCCESS, this._wrapper);
		this.caseId = SelectorEngine.findOne(CASE_ID, this.coachLearningJourneySuccess);
		this.coachLearningJourneySuccessButton = SelectorEngine.findOne(
			SELECTOR_SUCCESS_BUTTON,
			this.coachLearningJourneySuccess
		);

		this._errorContainer = SelectorEngine.findOne(SELECTOR_ERRROR_CONTAINER, this._wrapper);
		this._errorText = SelectorEngine.findOne('.error-text', this._errorContainer);
		this._closeErrorSnackBar = SelectorEngine.findOne('.js-dismiss-error', this._errorContainer);

		this._closeErrorSnackBar.addEventListener('click', () => {
			this._errorContainer.classList.add('u-hidden');
		});

		this.initEventHandlers();
	}

	initEventHandlers() {
		EventHandler.on(this.splashNext, Events.Click, () => {
			this.fetchSteps();
		});
		EventHandler.on(this.exitButton, Events.Click, () => {
			this.exitConfirmModal.show();
		});
		EventHandler.on(this.exitConfirmYes, Events.Click, () => {
			this.handleModalExit();
		});
		EventHandler.on(this.exitConfirmNo, Events.Click, () => {
			this.exitConfirmModal.hide();
		});
	}

	async fetchSteps() {
		try {
			const pageId = Manipulator.getDataAttribute(this._wrapper, 'page-id') || '0';
			const url = `/api/coachdetails/independent-Learning-Steps?pageId=${pageId}`;
			const response = await Request.get(
				url,
				{},
				{
					local: IS_LOCAL,
					req: {
						apiMode: ApiMode.LtaError
					}
				}
			);
			const { Steps, error } = response;
			if (Steps) {
				this.stepsData = Steps;
			}

			if (!!error?.hasError) {
				throw error;
			}
		} catch (error) {
			console.log('error', error);

			if (error.hasError) {
				console.log('Error : ', error);
			}
		}
	}

	initiateFormGenerator(stepsData) {
		this.splash.classList.add(ClassNames.Hidden);
		this.step.classList.remove(ClassNames.Hidden);

		this.formGenerator = new FormGenerator(this.questions, {
			questions: stepsData,
			totalSteps: 7,
			hasSeparator: false,
			formType: FormTypes.StepForm,
			themeColor: 'color-lta-blue',			
			isHideProgress: false,
			stepVisibility: [false, true, true, true, true, false, false],
			isBackButtonEnabled: true,
			mainPageElement: this.step,
			conditionalStepsVisiblity: true,
			backButtonHandlerFirstStep: () => {
				this.splash.classList.remove(ClassNames.Hidden);
				this.step.classList.add(ClassNames.Hidden);
			}
		});

		this.formGenerator.onChange((answers) => {
			this.prepareRequestObject(answers);
			this.changePayload(this.answerList);
		});
	}

	changePayload(answers) {
		let answerPayload = { ...answers };

		// Check Formal/Informal
		const isFormal = answers.hasOwnProperty('provider') || answers.hasOwnProperty('mentor') || answerPayload.hasOwnProperty('mentorType');

		//Time Learning
		const timeLearning = {
			date: answerPayload?.date,
			duration: answerPayload?.duration ? answerPayload?.duration : ''
		};
		delete answerPayload?.date;
		delete answerPayload?.duration;
		const timeKey = isFormal ? 'formalTimeLearning' : 'informalTimeLearning';
		answerPayload[timeKey] = { ...timeLearning };

		if (answers.hasOwnProperty('interstingThingAboutLearning')) {
			const reflect = {
				interstingThingAboutLearning: answers?.interstingThingAboutLearning
			};
			delete answerPayload?.interstingThingAboutLearning;
			answerPayload = { ...answerPayload, reflect };
		}

		if (answers.hasOwnProperty('subjectsCovered')) {
			const subjectsCovered = answerPayload.subjectsCovered?.map((item) => item.label);
			delete answerPayload?.subjectsCovered;
			answerPayload = { ...answerPayload, subjectsCovered };
		}

		if (answers.hasOwnProperty('level')) {
			const informalTournamentVisitLearningDetails = {
				name: answerPayload?.name,
				level: answerPayload?.level
			};
			delete answerPayload?.name;
			delete answerPayload?.level;
			answerPayload = { ...answerPayload, informalTournamentVisitLearningDetails };
		}

		if (answers.hasOwnProperty('author')) {
			const informalBookArticleLearningDetails = {
				title: answerPayload?.title,
				author: answerPayload?.author
			};
			delete answerPayload?.title;
			delete answerPayload?.author;
			answerPayload = { ...answerPayload, informalBookArticleLearningDetails };
		}

		if (answers.hasOwnProperty('aboutPodcast')) {
			const informalPodcastLearningDetails = {
				title: answerPayload?.title,
				aboutPodcast: answerPayload?.aboutPodcast
			};
			delete answerPayload?.title;
			delete answerPayload?.aboutPodcast;
			answerPayload = { ...answerPayload, informalPodcastLearningDetails };
		}

		if (answers.hasOwnProperty('provider')) {
			const formalLearningDetails = {
				title: answerPayload?.title,
				provider: answerPayload?.provider
			};
			delete answerPayload?.title;
			delete answerPayload?.provider;
			answerPayload = { ...answerPayload, formalLearningDetails };
		}

		if (answers.hasOwnProperty('informalProvider')) {
			const informalLearningDetails = {
				title: answerPayload?.title,
				provider: answerPayload?.informalProvider
			};
			delete answerPayload?.title;
			delete answerPayload?.informalProvider;
			answerPayload = { ...answerPayload, informalLearningDetails };
		}

		if (answers.hasOwnProperty('mentor') || answers.hasOwnProperty('mentorType')) {
			const formalMentorDetails = {
				type: answerPayload?.mentorType,
				name: answerPayload?.mentor?.name ? answerPayload?.mentor?.name : answerPayload.mentor,
				id: answerPayload?.mentor?.contactId
			};
			delete answerPayload?.mentor?.name;
			delete answerPayload?.mentor?.contactId;
			answerPayload = { ...answerPayload, formalMentorDetails };
		}
		console.log("Payload : ", answerPayload);
		this.submitSupportForm(answerPayload, timeKey);
	}

	prepareRequestObject(stepsAnswers) {
		this.answerList = {};
		for (const stepKey in stepsAnswers) {
			const stepAnswersObjects = stepsAnswers[stepKey];
			for (const questionKey in stepAnswersObjects) {
				const answerObject = stepAnswersObjects[questionKey];
				const formatObject = {};
				formatObject[answerObject[KEY_OBJECT]] = answerObject[VALUE_OBJECT];
				this.answerList = {
					source: ['learning submisssion'],
					...this.answerList,
					...formatObject
				};
			}
		}
	}

	showSuccessPage(caseId) {
		this.step.classList.add(HIDDEN_CLASS);
		this.coachLearningJourneySuccess.classList.remove(HIDDEN_CLASS);
		this.caseId.innerHTML = `Your reference number is: ${caseId}`;
	}

	async submitSupportForm(payload, timeKey) {
		startLoadingAnimation(true);
		
		const formData = new FormData();
		formData.append('json', JSON.stringify(payload));
		// checking if cerificate exists and appending its value in payloadsor
		if (payload.certificate !== (null || undefined) && payload.certificate?.length !== 0){
			for(let i=0; i < payload.certificate.length; i++) {
				formData.append('attachment', payload.certificate[i]);
			}
		}

		const endpoint =
			timeKey === 'formalTimeLearning'
				? '/api/coachdetails/submit-independent-learning?learningType=Formal'
				: '/api/coachdetails/submit-independent-learning?learningType=Informal';
		try {
			const url = endpoint;
			const responseData = await Request.postFormData(url, formData);

			const { success, caseId } = responseData;
			stopLoadingAnimation();
			if (success) {
				this.showSuccessPage(caseId);
			} else {
				this._errorContainer.classList.remove('u-hidden');
				this._errorText.innerHTML = Manipulator.getDataAttribute(this.questions, 'api-failed-error');
			}
		} catch {
			this._errorContainer.classList.remove('u-hidden');
			this._errorText.innerHTML = Manipulator.getDataAttribute(this.questions, 'api-failed-error');
			this._errorContainer.scrollIntoView();
		} finally {
			stopLoadingAnimation();
		}
	}

	handleModalExit() {
		// Go back to the page that launched the modal
		history.back();
	}
}

const globalAccreditationRenewal = new CoachesLearningJourney();
export default globalAccreditationRenewal;
